import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { KEY_CODES, KEY_DOWN } from '@outdoor-voices/ui/dist/config/constants';
import { memo, useEffect, useRef, useState } from 'react';
import { useQuery } from 'urql';
import PropTypes from 'prop-types';
import { PLP_IMAGE_SIZES } from '../../apps/collection/constants';
import addToCart from '../../utils/cart/addToCart';
import css from './ProductListItem.module.scss';
import Image from '../Image';
import Images from './Images';
import query from './queries';
import QuickAdd from './QuickAdd';
import Spinner from '../icons/Spinner';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var FeaturedImage = /*#__PURE__*/memo(function (_ref) {
  var alt = _ref.alt,
      image = _ref.image,
      lazyload = _ref.lazyload,
      title = _ref.title,
      url = _ref.url;
  return /*#__PURE__*/_jsx("a", {
    "aria-label": "Click to navigate to ".concat(title),
    className: css.featuredImage,
    href: url,
    children: /*#__PURE__*/_jsx(Image, _objectSpread(_objectSpread({}, image), {}, {
      alt: alt,
      lazyload: lazyload,
      sizes: PLP_IMAGE_SIZES,
      wrap: false
    }))
  });
});
FeaturedImage.propTypes = {
  alt: PropTypes.string.isRequired,
  image: PropTypes.shape({
    id: PropTypes.string,
    src: PropTypes.string
  }).isRequired,
  lazyload: PropTypes.bool,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
};

var ColorwayLoader = function ColorwayLoader(_ref2) {
  var availability = _ref2.availability,
      displayEditorial = _ref2.displayEditorial,
      id = _ref2.id,
      isMobile = _ref2.isMobile,
      product = _ref2.product,
      title = _ref2.title,
      url = _ref2.url;
  var quickAddRef = useRef(null);
  var rootRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showingQuickAddButtons = _useState2[0],
      setShowingQuickAddButtons = _useState2[1];

  var _useQuery = useQuery({
    query: query,
    variables: {
      id: id
    }
  }),
      _useQuery2 = _slicedToArray(_useQuery, 1),
      _useQuery2$ = _useQuery2[0],
      fetching = _useQuery2$.fetching,
      data = _useQuery2$.data,
      error = _useQuery2$.error;

  var focusOption = function focusOption(dir) {
    var buttons = quickAddRef.current.querySelectorAll('button:not(:disabled)');
    var items = Array.from(buttons);
    var index = Math.max(0, Math.min(items.indexOf(document.activeElement) + dir, buttons.length - 1));
    buttons[index].focus();
  };

  var onQuickAdd = function onQuickAdd() {
    setShowingQuickAddButtons(true);
  };

  var onQuickAddCancel = function onQuickAddCancel() {
    setShowingQuickAddButtons(false);
  };

  useEffect(function () {
    var onKeyDown = function onKeyDown(event) {
      var ARROW_DOWN = KEY_CODES.ARROW_DOWN,
          ARROW_LEFT = KEY_CODES.ARROW_LEFT,
          ARROW_RIGHT = KEY_CODES.ARROW_RIGHT,
          ARROW_UP = KEY_CODES.ARROW_UP,
          ESC = KEY_CODES.ESC,
          TAB = KEY_CODES.TAB;

      switch (event.key) {
        case ESC:
          setShowingQuickAddButtons(false);
          break;

        case ARROW_LEFT:
        case ARROW_UP:
          event.preventDefault();
          focusOption(-1);
          break;

        case ARROW_DOWN:
        case ARROW_RIGHT:
          event.preventDefault();
          focusOption(1);
          break;

        case TAB:
          event.preventDefault();
          break;

        default:
          break;
      }
    };

    var onQuickAddClickHide = function onQuickAddClickHide(event) {
      if (rootRef && rootRef.current.contains(event.target)) {
        return;
      }

      onQuickAddCancel();
    };

    var click = 'click';

    if (showingQuickAddButtons) {
      document.body.addEventListener(click, onQuickAddClickHide);
      document.removeEventListener(KEY_DOWN, onKeyDown);
      document.addEventListener(KEY_DOWN, onKeyDown, false);
    } else {
      document.body.removeEventListener(click, onQuickAddClickHide);
      document.removeEventListener(KEY_DOWN, onKeyDown);
    }

    return function () {
      document.body.removeEventListener(click, onQuickAddClickHide);
      document.removeEventListener(KEY_DOWN, onKeyDown);
    };
  }, [showingQuickAddButtons]);

  if (fetching) {
    return /*#__PURE__*/_jsx("div", {
      "aria-busy": "true",
      className: css.fetching,
      children: /*#__PURE__*/_jsx(Spinner, {})
    });
  }

  if (error || !data.colorway) {
    return false;
  }

  var colorway = data.colorway;
  var assets = colorway.assets,
      variantStacks = colorway.variantStacks; // Assign the availability to the colorway and variants based on data loaded from the storefront api.

  var available = false;
  variantStacks.forEach(function (_ref3) {
    var variant = _ref3.variant;
    variant.available = availability[variant.shopifyId];
    available = available || variant.available;
  });
  colorway.available = available;
  var variant = variantStacks[0].variant;
  var images = assets.edges.map(function (_ref4) {
    var node = _ref4.node;
    return node;
  }).filter(function (_ref5) {
    var image = _ref5.image;
    return image !== null;
  });

  if (displayEditorial && images.length > 1 && images[1].isEditorial) {
    // Swap the first and second images
    var _ref6 = [images[1], images[0]];
    images[0] = _ref6[0];
    images[1] = _ref6[1];
  }

  var imagesToDisplay = isMobile ? images : _toConsumableArray(images).splice(1);
  var quickAddEnabled = colorway.available;
  var sizes = variantStacks.map(function (vs) {
    return {
      label: vs.size,
      value: vs.variant.shopifyId,
      disabled: !vs.variant.available
    };
  });

  var onAddToBag = /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(variantId) {
      var addedVariant;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              addedVariant = variantStacks.find(function (vs) {
                return vs.variant.shopifyId === variantId;
              });
              setShowingQuickAddButtons(false);
              addToCart({
                _emsColorwayGid: id,
                _emsProductGid: product.id,
                price: addedVariant.variant.price,
                title: product.title,
                variantId: variantId
              });

            case 3:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function onAddToBag(_x) {
      return _ref7.apply(this, arguments);
    };
  }();

  var onClickAdd = sizes.length < 2 && variant.available ? function () {
    return onAddToBag(variant.shopifyId);
  } : onQuickAdd;
  return /*#__PURE__*/_jsxs("div", {
    ref: rootRef,
    children: [imagesToDisplay.length > 0 && /*#__PURE__*/_jsx("div", {
      className: css.images,
      children: /*#__PURE__*/_jsx(Images, {
        images: imagesToDisplay,
        title: title,
        url: url
      })
    }), quickAddEnabled && /*#__PURE__*/_jsx(QuickAdd, {
      onClickAdd: onClickAdd,
      showingQuickAddButtons: showingQuickAddButtons,
      sizes: sizes,
      onAddToBag: onAddToBag,
      ref: quickAddRef
    })]
  });
};

ColorwayLoader.propTypes = {
  availability: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]).isRequired,
  displayEditorial: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string
  }).isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
};

var Colorway = function Colorway(_ref8) {
  var availability = _ref8.availability,
      display = _ref8.display,
      image = _ref8.image,
      isMobile = _ref8.isMobile,
      displayEditorial = _ref8.displayEditorial,
      lazyload = _ref8.lazyload,
      product = _ref8.product,
      title = _ref8.title,
      id = _ref8.id,
      url = _ref8.url;

  if (!image) {
    return /*#__PURE__*/_jsx("div", {
      className: css.featuredImage,
      "aria-hidden": "true"
    });
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!isMobile && /*#__PURE__*/_jsx(FeaturedImage, {
      alt: title,
      image: image,
      lazyload: lazyload,
      title: title,
      url: url
    }), isMobile && /*#__PURE__*/_jsx("div", {
      className: css.featuredImage,
      "aria-hidden": "true"
    }), display && /*#__PURE__*/_jsx(ColorwayLoader, {
      availability: availability,
      displayEditorial: displayEditorial,
      id: id,
      isMobile: isMobile,
      product: product,
      title: title,
      url: url
    }, id)]
  });
};

Colorway.defaultProps = {
  display: true,
  image: false,
  displayEditorial: false
};
Colorway.propTypes = {
  availability: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]).isRequired,
  display: PropTypes.bool,
  displayEditorial: PropTypes.bool,
  id: PropTypes.string.isRequired,
  image: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool]),
  isMobile: PropTypes.bool.isRequired,
  lazyload: PropTypes.bool,
  product: PropTypes.shape({
    id: PropTypes.string
  }).isRequired,
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
};
export default Colorway;